import React, { useCallback, useEffect, useState } from "react";
import { MdPersonRemove, MdWarningAmber } from "react-icons/md";
import { toast } from "react-toastify";
import { PapelNoProjeto, PessoaDoCliente, ProjetoDoCliente } from "../../../../interfaces/ClienteEquipe";
import {
  definirPessoaNoProjeto,
  getPessoasDoCliente,
  getProjetosDoCliente,
  removerPessoaDoProjeto,
} from "../../../../services/clienteEquipeApi";
import { mensagemDoServidor } from "../../../../utils/mensagemDoServidor";

const selectCls =
  "rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const STATUS: Record<ProjetoDoCliente["status"], string> = {
  Active: "Ativo",
  Completed: "Concluído",
  Cancelled: "Cancelado",
};

const SEM_GESTAO = "Você não gere a equipe deste cliente.";

interface Props {
  orgId: number;
  /** `cliente.equipe.gerir`. Sem ele a aba é só leitura (pessoa do cliente com `portal.projects`). */
  podeGerir: boolean;
}

/** Aba "Projetos e equipe": donos e membros de cada projeto do cliente. Projeto ativo nunca fica sem dono. */
const EquipeTab: React.FC<Props> = ({ orgId, podeGerir }) => {
  const [projetos, setProjetos] = useState<ProjetoDoCliente[]>([]);
  const [pessoas, setPessoas] = useState<PessoaDoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Rascunho do formulário "adicionar", por projeto.
  const [novo, setNovo] = useState<Record<number, { pessoaId: string; papel: PapelNoProjeto }>>({});

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    Promise.all([getProjetosDoCliente(orgId), podeGerir ? getPessoasDoCliente(orgId) : Promise.resolve([])])
      .then(([lista, gente]) => {
        setProjetos(lista);
        setPessoas([...gente].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")));
      })
      .catch((e) => setErro(mensagemDoServidor(e, e?.response?.status === 403 ? SEM_GESTAO : "Não foi possível carregar os projetos do cliente. Tente de novo em instantes.")))
      .finally(() => setLoading(false));
  }, [orgId, podeGerir]);

  useEffect(carregar, [carregar]);

  /** Toda gravação: recusa do servidor (409/400) vai inteira para o toast; sucesso recarrega a lista. */
  const executar = async (acao: () => Promise<void>, sucesso: string, padrao: string) => {
    setOcupado(true);
    try {
      await acao();
      toast.success(sucesso);
      carregar();
    } catch (e) {
      toast.error(mensagemDoServidor(e, padrao));
    } finally {
      setOcupado(false);
    }
  };

  const semDono = projetos.filter((p) => p.semDono);

  if (loading) {
    return <div role="status" className="py-12 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>;
  }
  if (erro) {
    return (
      <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
        {erro}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {semDono.length > 0 && (
        <p
          role="status"
          className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 dark:border-amber-500/40 dark:bg-amber-900/20 dark:text-amber-200"
        >
          <MdWarningAmber className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {semDono.length} {semDono.length === 1 ? "projeto sem dono" : "projetos sem dono"}: {semDono.map((p) => p.nome).join(", ")}.
            {podeGerir
              ? " Defina uma pessoa do cliente como Dono em cada um."
              : " Peça a quem administra a conta do cliente para definir um Dono."}
          </span>
        </p>
      )}

      {projetos.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
          Este cliente ainda não tem projetos. Quando houver, os donos e membros de cada um aparecem aqui.
        </p>
      ) : (
        projetos.map((p) => {
          const naoEstao = pessoas.filter((x) => !p.pessoas.some((m) => m.pessoaId === x.pessoaId));
          const rascunho = novo[p.clientProjectId] ?? { pessoaId: "", papel: "Membro" as PapelNoProjeto };
          return (
            <section
              key={p.clientProjectId}
              aria-label={`Projeto ${p.nome}`}
              className={`rounded-xl border px-4 py-3 ${
                p.semDono
                  ? "border-amber-300 bg-amber-50/50 dark:border-amber-500/40 dark:bg-amber-900/10"
                  : "border-gray-100 bg-gray-50 dark:border-white/5 dark:bg-navy-900"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-bold text-navy-700 dark:text-white">{p.nome}</h3>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-700 dark:bg-navy-700 dark:text-gray-300">
                  {STATUS[p.status] ?? p.status}
                </span>
              </div>

              {p.pessoas.length === 0 ? (
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">Nenhuma pessoa neste projeto.</p>
              ) : (
                <ul className="mt-2 flex flex-col gap-1">
                  {p.pessoas.map((m) => (
                    <li key={m.pessoaId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="min-w-0 text-gray-800 dark:text-gray-200">
                        <span className="font-semibold">{m.nome}</span>{" "}
                        <span className="text-xs text-gray-600 dark:text-gray-400">({m.email})</span>
                      </span>
                      {podeGerir ? (
                        <span className="flex items-center gap-2">
                          <select
                            aria-label={`Papel de ${m.nome} no projeto ${p.nome}`}
                            className={selectCls}
                            value={m.papel}
                            disabled={ocupado}
                            onChange={(e) =>
                              executar(
                                () => definirPessoaNoProjeto(orgId, p.clientProjectId, m.pessoaId, e.target.value as PapelNoProjeto),
                                "Papel atualizado.",
                                "Não foi possível trocar o papel."
                              )
                            }
                          >
                            <option value="Dono">Dono</option>
                            <option value="Membro">Membro</option>
                          </select>
                          <button
                            type="button"
                            aria-label={`Remover ${m.nome} do projeto ${p.nome}`}
                            disabled={ocupado}
                            onClick={() =>
                              executar(
                                () => removerPessoaDoProjeto(orgId, p.clientProjectId, m.pessoaId),
                                "Pessoa removida do projeto.",
                                "Não foi possível remover a pessoa."
                              )
                            }
                            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-navy-700"
                          >
                            <MdPersonRemove className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </span>
                      ) : (
                        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 dark:bg-navy-800 dark:text-gray-300 dark:ring-white/10">
                          {m.papel}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {podeGerir && (
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-white/5">
                  <select
                    aria-label={`Pessoa para o projeto ${p.nome}`}
                    className={selectCls}
                    value={rascunho.pessoaId}
                    onChange={(e) => setNovo({ ...novo, [p.clientProjectId]: { ...rascunho, pessoaId: e.target.value } })}
                  >
                    <option value="">Escolha uma pessoa…</option>
                    {naoEstao.map((x) => (
                      <option key={x.pessoaId} value={x.pessoaId}>{x.nome}</option>
                    ))}
                  </select>
                  <select
                    aria-label={`Papel da nova pessoa no projeto ${p.nome}`}
                    className={selectCls}
                    value={rascunho.papel}
                    onChange={(e) => setNovo({ ...novo, [p.clientProjectId]: { ...rascunho, papel: e.target.value as PapelNoProjeto } })}
                  >
                    <option value="Membro">Membro</option>
                    <option value="Dono">Dono</option>
                  </select>
                  <button
                    type="button"
                    aria-label={`Adicionar ao projeto ${p.nome}`}
                    disabled={ocupado || !rascunho.pessoaId}
                    onClick={() =>
                      executar(
                        async () => {
                          await definirPessoaNoProjeto(orgId, p.clientProjectId, Number(rascunho.pessoaId), rascunho.papel);
                          setNovo((n) => ({ ...n, [p.clientProjectId]: { pessoaId: "", papel: "Membro" } }));
                        },
                        "Pessoa adicionada ao projeto.",
                        "Não foi possível adicionar a pessoa."
                      )
                    }
                    className="rounded-xl bg-ink-900 px-4 py-2 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
                  >
                    Adicionar
                  </button>
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
};

export default EquipeTab;
